import _ from 'lodash'
import { Spx, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import { color, resolveSpxValue } from '@wix/thunderbolt-commons'
import type {
	Size,
	Color,
	ThemeColor,
	Font,
	BoxShadow,
	BoxShadowLayer,
	Style,
	Opacity,
	Background,
	Border,
	TextShadow,
	TextShadowLayer,
} from './types'
import { parseFontStr } from '../fonts/fontsParser'

const unitValueConverter = (
	styleProp: Size,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
) => {
	if (styleProp.unit === 'spx' && isScalePropExperimentOn) {
		return resolveSpxValue(`${styleProp.value}spx`, themeSpx)
	}

	return `${styleProp.value}${styleProp.unit}`
}

const colorConverter = (styleProp: Color): string => color.getSplitRgbValuesStringWithoutAlpha(styleProp.value)!

const themeColorConverter = (styleProp: ThemeColor) => `var(--${styleProp.theme})`

const fontConverter = (
	styleProp: Font,
	themeFontMap: ThemeFontMap,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
) => {
	const { style, variant, weight, size, lineHeight, family, theme } = styleProp

	const fixedSize = size ? unitValueConverter(size, isScalePropExperimentOn, themeSpx) : '0'
	const fixedLineHeight = lineHeight ? unitValueConverter(lineHeight, isScalePropExperimentOn, themeSpx) : ''

	if (theme) {
		const themeFontValue = themeFontMap[theme]
		const themeFontObject = parseFontStr(themeFontValue)
		let themeSize = unitValueConverter(
			{ value: themeFontObject.size, unit: themeFontObject.sizeUnit, propertyType: 'Size' },
			isScalePropExperimentOn,
			themeSpx
		)

		let themeLineHeight = unitValueConverter(
			{ value: parseFloat(themeFontObject.lineHeight), unit: themeFontObject.lineHeight, propertyType: 'Size' },
			isScalePropExperimentOn,
			themeSpx
		)

		Object.entries(styleProp).forEach(([key, value]) => {
			switch (key) {
				case 'size':
					themeSize = fixedSize
					break
				case 'lineHeight':
					themeLineHeight = fixedLineHeight
					break
				default:
					_.set(themeFontObject, [key], value)
			}
		})

		return `${themeFontObject.style} ${themeFontObject.variant} ${themeFontObject.weight} ${themeSize}/${themeLineHeight} ${themeFontObject.family}`
	}

	return `${style} ${variant} ${weight} ${fixedSize}/${fixedLineHeight} ${family}`
}

const boxShadowConverter = (styleProp: BoxShadow) => {
	const layers = styleProp.layers
	return layers
		.map((layer: BoxShadowLayer) => {
			const { inset, offsetX, offsetY, blurRadius, spreadRadius, color: layerColor } = layer
			const insetStr = inset ? 'inset ' : ''
			const offsetXStr = unitValueConverter(offsetX)
			const offsetYStr = unitValueConverter(offsetY)
			const blurRadiusStr = blurRadius ? unitValueConverter(blurRadius) : ''
			const spreadRadiusStr = spreadRadius ? unitValueConverter(spreadRadius) : ''
			const colorStr = layerColor
				? layerColor.propertyType === 'Color'
					? layerColor.value
					: themeColorConverter(layerColor)
				: ''
			return `${insetStr}${offsetXStr} ${offsetYStr} ${blurRadiusStr} ${spreadRadiusStr} ${colorStr}`
		})
		.join(', ')
}

const opacityConverter = (styleProp: Opacity) => `${styleProp.value}`

const backgroundConverter = (styleProp: Background) => {
	const layers = styleProp.layers
	return layers
		.map((layer: Color | ThemeColor) => {
			return layer.propertyType === 'Color' ? layer.value : themeColorConverter(layer)
		})
		.join(', ')
}

const borderConverter = (styleProp: Border) => {
	const { width, style, color: borderColor } = styleProp
	const widthStr = unitValueConverter(width)
	const colorStr = borderColor.propertyType === 'Color' ? borderColor.value : themeColorConverter(borderColor)
	return `${widthStr} ${style} ${colorStr}`
}

const textShadowConverter = (styleProp: TextShadow) => {
	const layers = styleProp.layers
	return layers
		.map((layer: TextShadowLayer) => {
			const { offsetX, offsetY, blur, color: layerColor } = layer
			const offsetXStr = unitValueConverter(offsetX)
			const offsetYStr = unitValueConverter(offsetY)
			const blurRadiusStr = blur ? unitValueConverter(blur) : ''
			const colorStr = layerColor
				? layerColor.propertyType === 'Color'
					? layerColor.value
					: themeColorConverter(layerColor)
				: ''
			return `${offsetXStr} ${offsetYStr} ${blurRadiusStr} ${colorStr}`
		})
		.join(', ')
}

export const stylePropsToCssVars = (
	style: any,
	themeFontMap: ThemeFontMap,
	isScalePropExperimentOn: boolean = false,
	themeSpx: Spx | undefined = undefined
): Record<string, string | number> => {
	const styleProps = (style as Style).style.properties || (style as Style).style
	const cssVars = _.reduce(
		styleProps,
		(acc: Record<string, string | number>, value, prop) => {
			const styleType = typeof value === 'object' ? value.propertyType : undefined

			// we also need to store the original source value for getElementComputedStyle functionality
			acc[`--${prop}-source`] = JSON.stringify({ source: value })

			switch (styleType) {
				case 'Size':
					acc[`--${prop}`] = unitValueConverter(value as Size, isScalePropExperimentOn, themeSpx)
					return acc
				case 'Color':
					acc[`--${prop}`] = colorConverter(value as Color)
					if ((value as Color).value === 'transparent') {
						// set alpha channel to 0 explicitly when the color is transparent
						acc[`--alpha-${prop}`] = 0
					} else if (_.isUndefined(styleProps[`alpha-${prop}`])) {
						acc[`--alpha-${prop}`] = color.getColorAlpha((value as Color).value)!
					}
					return acc
				case 'ThemeColor':
					acc[`--${prop}`] = themeColorConverter(value as ThemeColor)
					if ((value as ThemeColor).alpha) {
						acc[`--alpha-${prop}`] = (value as ThemeColor).alpha!
					}
					return acc
				case 'Font':
					acc[`--${prop}`] = fontConverter(value as Font, themeFontMap, isScalePropExperimentOn, themeSpx)
					return acc
				case 'BoxShadow':
					acc[`--${prop}`] = boxShadowConverter(value as BoxShadow)
					return acc
				case 'Opacity':
					acc[`--${prop}`] = opacityConverter(value as Opacity)
					return acc
				case 'Background':
					acc[`--${prop}`] = backgroundConverter(value as Background)
					return acc
				case 'Border':
					acc[`--${prop}`] = borderConverter(value as Border)
					return acc
				case 'TextShadow':
					acc[`--${prop}`] = textShadowConverter(value as TextShadow)
					return acc
				default:
					acc[`--${prop}`] = value as string
			}
			return acc
		},
		{}
	)

	return cssVars
}
